import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { isEmpty } from "lodash";
import {
  Select,
  OutlinedInput,
  Menu,
  MenuItem,
  Grid,
  Popper,
  Paper,
  Box,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { ArrowForwardIos } from "@mui/icons-material";

const StyledSelect = styled(Select)(({ theme }) => ({
    height: '40px',
    width: '100%', // Add this
    '& .MuiSelect-select': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
      color: theme.palette.text.primary,
      height: '40px',
      padding: '8px 14px',
      display: 'flex',
      alignItems: 'center',
      lineHeight: '1.4'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.secondary
    },
    '& .MuiSelect-outlined': {
      minHeight: '40px'
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 14px',
      height: '24px'
    }
  }));


const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  minWidth: 200,
  height: 32,
  fontSize: 14,
  margin: theme.spacing(0, 1),
  padding: theme.spacing(0.75, 1),
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
  color: theme.palette.text.primary,
  '& .MuiGrid-root': {
    // width: '100%'
  },
  '& .label-text': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '250px' // Adjust based on menu width
  },
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : 'rgba(0, 0, 0, 0.04)',
    borderRadius: 4
  }
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.5),
    width: '200px',  // Decreased width
    minWidth: '150px', // Decreased minWidth
    boxShadow: theme.shadows[4]
  },
  '& .MuiMenuItem-root': {
    minHeight: '28px',
    height: '28px',
    padding: theme.spacing(0.25, 1),
    fontSize: '14px',
    lineHeight: '1.2'
  },
  '& .MuiList-root': {
    padding: theme.spacing(0.25, 0)
  }
}));

const StyledArrowIcon = styled(ArrowForwardIos)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary
}));
const StyledPopper = styled(Popper)(({ theme }) => ({
    zIndex: theme.zIndex.modal + 1,
    '& .MuiPaper-root': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
      color: theme.palette.text.primary,
      marginLeft: theme.spacing(1),
      minWidth: 180,
      boxShadow: theme.shadows[4]
    }
  }));
  const SubMenu = React.memo(({ 
    open, 
    anchorEl, 
    options, 
    onSelect, 
    parentLabel, 
    parentValue 
  }) => (
    <StyledMenu
    open={open}
    anchorEl={anchorEl}
    anchorOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'left'
    }}
    sx={{
      pointerEvents: 'none',
      zIndex: theme => theme.zIndex.modal + 2,
      '& .MuiPaper-root': {
        pointerEvents: 'auto',
        marginLeft: 1,
        transform: 'none !important'
      }
    }}
    >
      {options?.map(option => (
        <StyledMenuItem
          key={option.value}
          onClick={() => onSelect({
            label: option.label,
            value: option.value,
            parent: { label: parentLabel, value: parentValue }
          })}
        >
          {option.label}
        </StyledMenuItem>
      ))}
    </StyledMenu>
  ));
  
  const ReactNestedSelect = forwardRef(({ crmFields = [], onChange, fullWidth = true, placeholder = "Select", defaultValue = {}, },ref) => {
    const [anchorEl, setAnchorEl] = useState({});
    const [selectedValue, setSelectedValue] = useState({});
    const [activeItem, setActiveItem] = useState(null);
    const parentRef = useRef({});
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);

    const filterOptions = (options, term) => {
      
      if (!term) return options;
      return options.filter(option => {
        const parentMatch = option.label.toLowerCase().includes(term.toLowerCase());
        const childMatch = option.options?.some(child => 
          child.label.toLowerCase().includes(term.toLowerCase())
        );
        return parentMatch || childMatch;
      });
    };

    const handleSelect = (selectedObject) => {
      onChange(selectedObject);
      setSelectedValue(selectedObject);
      // setSearchTerm(selectedObject.label || ''); // Update search with selected value
      setAnchorEl({});
      setActiveItem(null);
      
    };
    useImperativeHandle(ref, () => ({
        reset() {
          setSelectedValue({});
        }
      }));
    useEffect(() => {
        console.log('defaultValue',defaultValue);
        if (defaultValue && defaultValue.value && !selectedValue.value) {
            setSelectedValue(defaultValue);
          }
        }, [defaultValue, selectedValue]);

    const handleMouseEnter = (e, key) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveItem(key);
        setAnchorEl(prev => ({ 
          ...prev, 
          [key]: parentRef.current[key] 
        }));
      };
  
    const handleMouseLeave = () => {
      setActiveItem(null);
      setAnchorEl({});
    };

    const handleSearchFocus = (e) => {
      e.stopPropagation();
      setIsSearchFocused(true);
      setAnchorEl((prev) => (prev.main ? prev : { main: e.currentTarget }));
    };
  
    const handleSearchBlur = (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setIsSearchFocused(false);
      }
    };
  
    const handleSearchChange = (e) => {
      console.log('enter',e);
      e.stopPropagation();
      // Remove any special handling for "a"
      
      setSearchTerm(e.target.value);
      setAnchorEl(prev => prev.main ? prev : { main: e.currentTarget });
    };

    const renderSelectedValue = (selected) => {
      console.log('selected',selected);
        if (!selected || !selected.label) return placeholder;
        if (selected.parent && selected.parent.label) {
            console.log(selected);
          return `${selected.parent.label} > ${selected.label}`;
        }
        return selected.label;
      };
    return (
      <StyledSelect
        value={selectedValue.label ?? placeholder}
        renderValue={() => renderSelectedValue(selectedValue)}
        open={!isEmpty(anchorEl) || isSearchFocused}
        onOpen={(e) => {
          setAnchorEl({ main: e.target });
          // Keep any existing search term
          if (selectedValue.label) {
            setSearchTerm(selectedValue.label);
          }
        }}
        onClose={(e) => {
          // Only close if not interacting with search
          if (!isSearchFocused && !e.currentTarget.contains(e.relatedTarget)) {
            handleMouseLeave();
          }
        }}
        fullWidth={fullWidth}
        input={<OutlinedInput fullWidth />} // Add fullWidth here
        MenuProps={{
          elevation: 0,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          sx: {
            '& .MuiPaper-root': {
              border: `1px solid ${theme => theme.palette.divider}`,
              marginTop: '8px',
              width: '200px', // Decreased width
              minWidth: '150px', // Decreased minWidth
              maxHeight: '300px', // Add max height
              overflowY: 'auto',  // Enable vertical scroll
              transform: 'none !important'
            },
            '& .MuiList-root': {
              padding: '4px 0'
            }
          },
          slotProps: {
            paper: {
              style: {
                transform: 'none !important'
              }
            }
          }
        }}
      >
        <Box 
          sx={{ 
            p: 1, 
            position: 'sticky', 
            top: 0, 
            bgcolor: 'background.paper', 
            zIndex: 1 
          }}
          onClick={e => e.stopPropagation()} // Prevent select close
        >
          <OutlinedInput
            fullWidth
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              setAnchorEl(prev => prev.main ? prev : { main: e.currentTarget });
            }}
            onKeyDown={(e) => {
              // Prevent select from capturing keystrokes
              e.stopPropagation(); 
            }}
            onFocus={(e) => {
              setIsSearchFocused(true);
              setAnchorEl(prev => prev.main ? prev : { main: e.currentTarget });
            }}
            onBlur={handleSearchBlur}
            onClick={(e) => handleSearchFocus(e)}
            sx={{
              height: 36,
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px'
              }
            }}
          />
        </Box>
        <StyledMenuItem 
      value=""
      sx={{
        color: 'text.secondary',
        // fontStyle: 'italic'
      }}
      onClick={() => {
        handleSelect({});
        setSelectedValue({});
      }}
    >
      <Grid container alignItems="center">
        <Grid item>All Universities & Campuses</Grid>
      </Grid>
    </StyledMenuItem>
        {filterOptions(crmFields, searchTerm).map(({ value, label, options }) => {
          const key = label + value;
          return (
            <div
              key={key}
              onMouseEnter={(e) => handleMouseEnter(e, key)}
              onMouseLeave={handleMouseLeave}
              ref={(el) => { if (el) parentRef.current[key] = el; }}
            >
              
              <StyledMenuItem
                value={value}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!options?.length) {
                    handleSelect({ label, value });
                  }
                }}
                title={label} // Add tooltip
              >
                <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
                  <Grid item xs className="label-text">
                    {label}
                  </Grid>
                  {options?.length>0 && (
                    <Grid item>
                      <StyledArrowIcon />
                    </Grid>
                  )}
                </Grid>
              </StyledMenuItem>
              
              {Array.isArray(options) && options.length > 0 && (
                <SubMenu
                  open={activeItem === key && Boolean(anchorEl[key])}
                  anchorEl={parentRef.current[key]}
                  options={options}
                  onSelect={handleSelect}
                  parentLabel={label}
                  parentValue={value}
                />
              )}
            </div>
          );
        })}
      </StyledSelect>
    );
  });

export default ReactNestedSelect;